import React, { useState, useEffect } from 'react';
import { 
  X, Sparkles, ChevronRight, ChevronLeft, Clock, Calendar, 
  Award, BookOpen, Check, Target, Headphones, Mic, Edit3, HelpCircle 
} from 'lucide-react';
import { studyPlanService, StudyPlan } from '../services/studyPlanService';
import T from './T';


interface StudyPlanOnboardingModalProps {
  isOpen: boolean;
  onClose: () => void;
  userId: string | undefined;
  onPlanCreated: (plan: StudyPlan, openRoadmap: boolean) => void;
}

const LANGUAGES = [
  { code: 'en', name: 'Inglês', flag: '🇺🇸', desc: 'Mais de 10.000 exercícios, áudios pré-gravados e audiobooks sincronizados' },
  { code: 'fr', name: 'Francês', flag: '🇫🇷', desc: 'Fonética refinada, gramática progressiva e repertório de palavras' },
  { code: 'it', name: 'Italiano', flag: '🇮🇹', desc: 'Trilhas completas de fonética, escrita e compreensão' },
  { code: 'es', name: 'Espanhol', flag: '🇪🇸', desc: 'Gramática do A1 ao C2, flashcards, pronúncia, escuta e escrita' },
  { code: 'de', name: 'Alemão', flag: '🇩🇪', desc: 'Casos, verbos e sintaxe do A1 ao C2 com flashcards e pronúncia' },
  { code: 'ru', name: 'Russo', flag: '🇷🇺', desc: 'Alfabeto cirílico, casos e aspecto verbal do A1 ao C2' },
  { code: 'hu', name: 'Húngaro', flag: '🇭🇺', desc: 'Sufixos, conjugações e ordem das palavras do A1 ao C2' },
  { code: 'pt', name: 'Português', flag: '🇧🇷', desc: 'Gramática do português do A1 ao C2 com flashcards e escrita' }
];

const LEVELS = [
  { code: 'A1', name: 'A1 - Iniciante', desc: 'Para quem está começando do absoluto zero' },
  { code: 'A2', name: 'A2 - Básico', desc: 'Conhece palavras essenciais e frases do cotidiano' },
  { code: 'B1', name: 'B1 - Intermediário', desc: 'Compreende conversas e se expressa com frases completas' },
  { code: 'B2', name: 'B2 - Independente', desc: 'Fluência intermediária, quer ampliar nuances e vocabulário' },
  { code: 'C1', name: 'C1 - Avançado', desc: 'Domínio sólido, refinando escrita complexa e articulação' }
];

const TIME_OPTIONS = [
  { minutes: 5, label: '5 min / dia', desc: 'Ritmo rápido para manter constância diária' },
  { minutes: 10, label: '10 min / dia', desc: 'Consistente e prático para o dia a dia' },
  { minutes: 15, label: '15 min / dia', desc: 'Equilíbrio ideal entre vocabulário e pronúncia' },
  { minutes: 30, label: '30 min / dia', desc: 'Recomendado para evolução acelerada' },
  { minutes: 60, label: '60 min / dia', desc: 'Imersão intensiva e rápida fluência' },
  { minutes: 90, label: '90 min / dia', desc: 'Dedicação profunda e domínio acelerado' },
  { minutes: 120, label: '120 min / dia', desc: 'Imersão intensiva de alto rendimento' }
];

export const StudyPlanOnboardingModal: React.FC<StudyPlanOnboardingModalProps> = ({
  isOpen,
  onClose,
  userId,
  onPlanCreated
}) => {
  const [step, setStep] = useState<number>(1);
  const [selectedLang, setSelectedLang] = useState<string>('en');
  const [selectedLevel, setSelectedLevel] = useState<string>('A1');
  const [dailyMinutes, setDailyMinutes] = useState<number>(30);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [existingLangs, setExistingLangs] = useState<string[]>([]);

  // Idiomas que já têm plano não podem ser criados de novo
  useEffect(() => {
    if (!isOpen) return;
    setStep(1);
    let alive = true;
    studyPlanService.getUserPlans(userId).then(plans => {
      if (!alive) return;
      const langs = plans.map(p => (p.language_code || '').toLowerCase().split('-')[0]);
      setExistingLangs(langs);
      const firstFree = LANGUAGES.find(l => !langs.includes(l.code));
      if (firstFree) setSelectedLang(cur => (langs.includes(cur) ? firstFree.code : cur));
    }).catch(() => {});
    return () => { alive = false; };
  }, [isOpen, userId]);

  if (!isOpen) return null;

  const selectedLangHasPlan = existingLangs.includes(selectedLang);

  const projections = studyPlanService.calculateProjections(selectedLevel, dailyMinutes);

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '—';
    try {
      const [year, month, day] = dateStr.split('-');
      const d = new Date(Number(year), Number(month) - 1, Number(day));
      return d.toLocaleDateString('pt-BR', { month: 'short', year: 'numeric' });
    } catch (e) {
      return dateStr;
    }
  };

  const handleFinish = async (openRoadmap: boolean) => {
    setIsSaving(true);
    try {
      const today = new Date().toISOString().split('T')[0];
      const newPlan: StudyPlan = {
        id: `plan_${userId || 'guest'}_${selectedLang}`,
        user_id: userId || 'guest',
        language_code: selectedLang,
        current_level: selectedLevel,
        daily_goal_minutes: dailyMinutes,
        current_day_index: 1,
        start_date: today,
        projected_a2_date: projections.a2Date,
        projected_b1_date: projections.b1Date,
        projected_b2_date: projections.b2Date,
        projected_c1_date: projections.c1Date,
        projected_c2_date: projections.c2Date,
        total_minutes_studied: 0
      };

      await studyPlanService.savePlan(newPlan);
      onPlanCreated(newPlan, openRoadmap);
      onClose();
    } catch (err) {
      console.error('Erro ao salvar plano:', err);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/30 backdrop-blur-md animate-in fade-in duration-200">
      <div 
        className="bg-surface w-full max-w-xl rounded-[28px] p-6 sm:p-8 border border-black/[0.06] dark:border-white/[0.06] flex flex-col max-h-[90vh] overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between pb-4 border-b border-black/[0.06] dark:border-white/[0.06] shrink-0">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-primary/10 text-primary rounded-2xl">
              <Target size={22} />
            </div>
            <div>
              <h3 className="text-lg font-medium dark:text-ink tracking-[-0.03em]"><T k="ui.onb.create" /></h3>
              <p className="text-[10px] font-normal text-muted uppercase font-mono tracking-[0.14em]">ETAPA {step} DE 4</p>
            </div>
          </div>
          <button 
            onClick={onClose} 
            className="p-2.5 text-muted hover:text-muted rounded-full transition-colors cursor-pointer"
          >
            <X size={20} />
          </button>
        </div>

        {/* Content Body */}
        <div className="flex-1 overflow-y-auto custom-scrollbar py-6 space-y-6">
          {/* ETAPA 1: IDIOMA */}
          {step === 1 && (
            <div className="space-y-4">
              <div className="space-y-1">
                <h4 className="text-xl font-semibold text-ink"><T k="ui.onb.whichLang" /></h4>
                <p className="text-xs text-muted font-medium"><T k="ui.onb.whichLangHint" /></p>
              </div>

              <div className="space-y-2.5 pt-2">
                {LANGUAGES.map(lang => (
                  <button
                    key={lang.code}
                    onClick={() => setSelectedLang(lang.code)}
                    className={`w-full p-4 rounded-2xl border text-left flex items-center gap-4 transition-all cursor-pointer ${
                      selectedLang === lang.code
                        ? 'border-primary bg-primary/5 dark:bg-primary/10 ring-2 ring-primary/20'
                        : 'border-black/[0.06] dark:border-white/[0.06] hover:border-black/[0.06] bg-surface '
                    }`}
                  >
                    <span className="text-3xl">{lang.flag}</span>
                    <div className="flex-1">
                      <h5 className="font-semibold text-sm text-ink">{lang.name}</h5>
                      {existingLangs.includes(lang.code) && (
                        <p className="text-[11px] text-muted font-medium mt-0.5">Você já tem um plano neste idioma</p>
                      )}
                    </div>
                    {selectedLang === lang.code && <Check size={20} className="text-primary shrink-0" />}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* ETAPA 2: NÍVEL */}
          {step === 2 && (
            <div className="space-y-4">
              <div className="space-y-1">
                <h4 className="text-xl font-semibold text-ink"><T k="ui.onb.level" /></h4>
                <p className="text-xs text-muted font-medium"><T k="ui.onb.levelHint" /></p>
              </div>

              <div className="space-y-2.5 pt-2">
                {LEVELS.map(lvl => {
                  const isSelected = selectedLevel === lvl.code;
                  return (
                    <button
                      key={lvl.code}
                      onClick={() => setSelectedLevel(lvl.code)}
                      className={`w-full p-4 rounded-2xl border text-left transition-all duration-300 cursor-pointer overflow-hidden ${
                        isSelected
                          ? 'border-primary bg-primary/5 dark:bg-primary/10 ring-2 ring-primary/20'
                          : 'border-black/[0.06] dark:border-white/[0.06] hover:border-black/[0.06] bg-surface '
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <h5 className="font-semibold text-sm text-ink">{lvl.name}</h5>
                        {isSelected && <Check size={20} className="text-primary shrink-0" />}
                      </div>
                      <div 
                        className={`grid transition-all duration-300 ease-in-out ${
                          isSelected ? 'grid-rows-[1fr] opacity-100 mt-2 pt-2 border-t border-primary/10 dark:border-primary/20' : 'grid-rows-[0fr] opacity-0'
                        }`}
                      >
                        <div className="overflow-hidden">
                          <p className="text-xs text-muted font-medium leading-relaxed">
                            {lvl.desc}
                          </p>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>

              <div className="p-3.5 bg-tint-stone/60 rounded-2xl border border-black/[0.06] dark:border-white/[0.06] text-center">
                <p className="text-xs text-muted">
                  Dúvida sobre o nível? Você também pode fazer o{' '}
                  <a href="/level-test" className="text-primary font-semibold underline hover:opacity-80">
                    Teste Rápido de Nível
                  </a>.
                </p>
              </div>
            </div>
          )}

          {/* ETAPA 3: TEMPO DIÁRIO */}
          {step === 3 && (
            <div className="space-y-4">
              <div className="space-y-1">
                <h4 className="text-xl font-semibold text-ink"><T k="ui.onb.time" /></h4>
                <p className="text-xs text-muted font-medium"><T k="ui.onb.timeHint" /></p>
              </div>

              <div className="space-y-2.5 pt-2">
                {TIME_OPTIONS.map(opt => (
                  <button
                    key={opt.minutes}
                    onClick={() => setDailyMinutes(opt.minutes)}
                    className={`w-full p-4 rounded-2xl border text-left flex items-center justify-between transition-all cursor-pointer ${
                      dailyMinutes === opt.minutes
                        ? 'border-primary bg-primary/5 dark:bg-primary/10 ring-2 ring-primary/20'
                        : 'border-black/[0.06] dark:border-white/[0.06] hover:border-black/[0.06] bg-surface '
                    }`}
                  >
                    <div className="flex items-center gap-3.5">
                      <div className="p-2.5 rounded-xl bg-tint-stone/60 text-ink">
                        <Clock size={18} />
                      </div>
                      <div>
                        <h5 className="font-semibold text-sm text-ink">{opt.label}</h5>
                        <p className="text-xs text-muted font-medium mt-0.5">{opt.desc}</p>
                      </div>
                    </div>
                    {dailyMinutes === opt.minutes && <Check size={20} className="text-primary shrink-0" />}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* ETAPA 4: PROJEÇÃO & CONFIRMAÇÃO */}
          {step === 4 && (
            <div className="space-y-5">
              <div className="space-y-1">
                <h4 className="text-xl font-semibold text-ink flex items-center gap-2">
                  <span><T k="ui.onb.projection" /></span>
                  <Sparkles size={20} className="text-warn" />
                </h4>
                <p className="text-xs text-muted font-medium">
                  Com base no Quadro Europeu (CEFR) e na sua dedicação de <strong className="text-primary">{dailyMinutes} min/dia</strong>:
                </p>
              </div>

              {/* Card do Próximo Nível */}
              {(() => {
                const getNextLevelInfo = () => {
                  switch (selectedLevel) {
                    case 'A1':
                      return { tag: 'PRÓXIMO NÍVEL', name: 'Nível A2 (Básico)', date: projections.a2Date, color: 'text-ok' };
                    case 'A2':
                      return { tag: 'PRÓXIMO NÍVEL', name: 'Nível B1 (Independente)', date: projections.b1Date, color: 'text-tintd-sky' };
                    case 'B1':
                      return { tag: 'PRÓXIMO NÍVEL', name: 'Nível B2 (Fluência Prática)', date: projections.b2Date, color: 'text-tintd-lavender' };
                    case 'B2':
                      return { tag: 'PRÓXIMO NÍVEL', name: 'Nível C1 (Avançado)', date: projections.c1Date, color: 'text-tintd-lavender' };
                    case 'C1':
                    default:
                      return { tag: 'PRÓXIMO NÍVEL', name: 'Nível C2 (Domínio Pleno)', date: projections.c2Date, color: 'text-warn' };
                  }
                };
                const nextMilestone = getNextLevelInfo();

                return (
                  <div className="p-5 rounded-2xl bg-transparent border border-black/[0.06] dark:border-white/[0.06] flex items-center justify-between">
                    <div>
                      <span className={`text-[10px] font-normal uppercase font-mono tracking-[0.14em] ${nextMilestone.color}`}>{nextMilestone.tag}</span>
                      <h5 className="font-semibold text-lg text-ink mt-0.5">{nextMilestone.name}</h5>
                    </div>
                    <div className="text-right">
                      <span className="text-[10px] font-normal uppercase tracking-[0.14em] font-mono text-muted block"><T k="ui.onb.forecast" /></span>
                      <span className="text-sm font-semibold text-primary">📅 {formatDate(nextMilestone.date)}</span>
                    </div>
                  </div>
                );
              })()}

              {/* Métodos inclusos */}
              <div className="p-4 rounded-2xl bg-primary/5 dark:bg-primary/10 border border-primary/20 space-y-2">
                <span className="text-[10px] font-normal uppercase font-mono tracking-[0.14em] text-primary block"><T k="ui.onb.willPractice" /></span>
                <div className="grid grid-cols-2 gap-2 text-xs font-bold text-ink">
                  <div className="flex items-center gap-2"><span>🎯</span> <T k="ui.onb.vocabPhrases" /></div>
                  <div className="flex items-center gap-2"><span>🎙️</span> <T k="ui.onb.phonetics" /></div>
                  <div className="flex items-center gap-2"><span>🎧</span> Listening Interativo</div>
                  <div className="flex items-center gap-2"><span>✍️</span> <T k="writing_page.practice" /></div>
                  <div className="flex items-center gap-2"><span>📖</span> <T k="ui.onb.grammar" /></div>
                  {selectedLang === 'en' && <div className="flex items-center gap-2"><span>📻</span> Audiobooks Sincronizados</div>}
                  <div className="flex items-center gap-2"><span>⚠️</span> <T k="ui.onb.errorMastery" /></div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer Navigation Buttons */}
        <div className="pt-4 border-t border-black/[0.06] dark:border-white/[0.06] flex items-center justify-between gap-3 shrink-0">
          {step > 1 ? (
            <button
              onClick={() => setStep(prev => prev - 1)}
              className="px-5 py-3 rounded-2xl border border-black/[0.06] dark:border-white/[0.06] text-ink font-medium text-[13px] hover:bg-tint-stone/60 transition-all cursor-pointer flex items-center gap-2"
            >
              <ChevronLeft size={16} /> Voltar
            </button>
          ) : <div />}

          {step < 4 ? (
            <button
              onClick={() => setStep(prev => prev + 1)}
              disabled={selectedLangHasPlan}
              title={selectedLangHasPlan ? 'Você já tem um plano neste idioma' : undefined}
              className="px-7 py-3 bg-primary text-slate-900 rounded-2xl font-medium text-[13px] hover:scale-[1.02] active:scale-95 transition-all cursor-pointer flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:scale-100"
            >
              Próximo <ChevronRight size={16} />
            </button>
          ) : (
            <div className="flex items-center gap-2">
              <button
                disabled={isSaving}
                onClick={() => handleFinish(true)}
                className="px-4 py-3 border border-black/[0.06] text-ink rounded-2xl font-medium text-[13px] hover:bg-tint-stone/60 transition-all cursor-pointer"
              >
                Ver Roteiro Completo
              </button>
              <button
                disabled={isSaving}
                onClick={() => handleFinish(false)}
                className="px-6 py-3 bg-primary text-slate-900 rounded-2xl font-medium text-[13px] hover:scale-[1.02] active:scale-95 transition-all cursor-pointer flex items-center gap-2"
              >
                Iniciar Agora <Sparkles size={16} />
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
